import { useEffect, useRef } from 'react';
import { Image } from '@sitecore-jss/sitecore-jss-nextjs';
import { sanitize } from 'isomorphic-dompurify';

import * as icons from '@gn-audio/icons';
import { Button, JabraIcon } from '@gn-audio/jss-components';
import { ProductType } from '@gn-audio/jss-core';

import { Price } from 'composables/Product/Buy/Price/Price';
import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';

import { CompareTableColumnProps, ProductRow } from './compareTableSpotTypes';

export const CompareTableColumn = ({
  familyWithPrice,
  iconBackgroundColor,
  maxHeight,
  onHeightChange,
}: CompareTableColumnProps) => {
  const { t } = useFallbackTranslation();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const [entry] = entries;
      if (entry) {
        onHeightChange(entry.contentRect.height);
      }
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [onHeightChange]);

  const iconBackgroundColorStyle = {
    backgroundColor: iconBackgroundColor,
  };

  const priceAndDescriptionStyle = {
    height: maxHeight > 0 ? `${maxHeight}px` : 'auto',
    overflow: 'hidden',
  };

  return (
    <div className="mx-5 flex flex-col items-center">
      <div className="relative">
        <div className="absolute inset-0 z-0 flex items-center justify-center">
          <div className="h-[130px] w-[130px] rounded-full" style={iconBackgroundColorStyle} />
        </div>
        <a className="relative z-10">
          <Image
            className="size-[199px]"
            field={{
              src: familyWithPrice.thumbnailUrl as string,
              alt: familyWithPrice.title as string,
            }}
          />
        </a>
      </div>
      <section className="flex flex-col gap-4 pb-6 text-center">
        <h2 className="heading-4">{familyWithPrice.title}</h2>
        <div style={priceAndDescriptionStyle} ref={contentRef}>
          <p
            className="paragraph-large opacity-60"
            dangerouslySetInnerHTML={{
              __html: sanitize(familyWithPrice.uniqueSellingPoint as string),
            }}
          />
          <Price
            className="paragraph-large flex min-h-[20px] justify-center font-normal opacity-60"
            productType={ProductType.Family}
            price={familyWithPrice.price}
          />
        </div>
      </section>
      <div className="flex flex-col items-center justify-start gap-4">
        <Button
          className="self-center"
          aria-label={`${t('system.BuyNow')} ${familyWithPrice.productName}`}
          asLink={true}
          variant="primary"
          size="l"
          href={familyWithPrice.pageUrl + '/buy'}
          icon={<JabraIcon Icon={icons.icon_basket} />}
          iconPlacement="right"
        >
          {t('system.BuyNow')}
        </Button>
        <Button
          className="underline-offset-8"
          asLink
          href={familyWithPrice.pageUrl}
          variant="quaternary"
          linkUnderline
          size="m"
        >
          {t('system.ReadMore')}
        </Button>
      </div>

      <div className="flex w-full flex-col gap-4 text-center">
        {familyWithPrice.productRow.map(({ headline, icon, value, id }: ProductRow) => (
          <div className="flex w-full flex-col gap-4 border-b-2 border-b-black/10 py-6" key={id}>
            {icon?.value && <JabraIcon className="size-8 w-full" Icon={icons[icon.value]} />}
            <span className="text-base opacity-60">{headline?.value}</span>
            <p className="text-base font-bold">{value?.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
